/**
 * Methodology Intelligence — deterministic scoring engine.
 *
 * Answers to a small set of plain-language factors are turned into three
 * independent fit scores (Agile / Waterfall / Hybrid) that sum to 100. The model
 * is pure and reproducible ("same inputs, same result") so the recommendation is
 * defensible; a separate AI step (edge function recommend-methodology) turns the
 * scores into a narrative. This file never calls AI.
 *
 * How the scores are derived:
 *  - Each factor is answered 1–5. One end pulls toward Agile, the other toward
 *    Waterfall (see `agileHigh`). We convert each answer to a polarity in
 *    [-1, +1] (+ = Agile end, − = Waterfall end), weighted per factor.
 *  - agilePull / waterfallPull = summed magnitude on each side.
 *  - The CONTESTED mass (min of the two pulls) is what makes a project Hybrid —
 *    real tension where strong signals pull both ways, not merely middling
 *    answers. Hybrid = 2 × contested; Agile / Waterfall = each side's net beyond
 *    the contested mass. All three normalize to percentages.
 */

import type { Methodology } from '@/types';

export interface MethodologyFactor {
  key: string;
  label: string;
  hint: string;
  /** Slider endpoint labels (value 1 → lowLabel, value 5 → highLabel). */
  lowLabel: string;
  highLabel: string;
  /** true = a HIGH answer (5) leans Agile; false = a HIGH answer leans Waterfall. */
  agileHigh: boolean;
  weight: number;
}

/** The 8 factors. Requirements clarity + expected change are the strongest
 *  discriminators, so they carry slightly more weight. */
export const METHODOLOGY_FACTORS: MethodologyFactor[] = [
  { key: 'reqClarity', label: 'Requirements clarity', hint: 'How well-defined and stable are the requirements?', lowLabel: 'Evolving / uncertain', highLabel: 'Stable & known', agileHigh: false, weight: 1.25 },
  { key: 'expectedChange', label: 'Expected change', hint: 'How often will scope change during delivery?', lowLabel: 'Rarely', highLabel: 'Frequently', agileHigh: true, weight: 1.25 },
  { key: 'stakeholderEngagement', label: 'Stakeholder engagement', hint: 'How available are stakeholders for feedback?', lowLabel: 'Periodic sign-offs', highLabel: 'Continuous', agileHigh: true, weight: 1 },
  { key: 'deliveryStyle', label: 'Delivery style', hint: 'Can value ship incrementally, or all at the end?', lowLabel: 'One big delivery', highLabel: 'Incremental releases', agileHigh: true, weight: 1 },
  { key: 'complianceLoad', label: 'Compliance & documentation', hint: 'How heavy are audit / documentation needs?', lowLabel: 'Light', highLabel: 'Heavy / audited', agileHigh: false, weight: 1 },
  { key: 'fixedConstraints', label: 'Fixed constraints', hint: 'Are scope, budget, and deadline fixed up front?', lowLabel: 'Flexible', highLabel: 'All fixed', agileHigh: false, weight: 1 },
  { key: 'teamExperience', label: "Team's iterative experience", hint: 'How experienced is the team with iterative delivery?', lowLabel: 'Low', highLabel: 'High', agileHigh: true, weight: 1 },
  { key: 'dependencies', label: 'Dependencies & sequencing', hint: 'How many external, tightly-ordered dependencies?', lowLabel: 'Few / self-contained', highLabel: 'Many / tightly ordered', agileHigh: false, weight: 1 },
];

export type MethodologyAnswers = Record<string, number>;

export type Confidence = 'low' | 'moderate' | 'high';

export interface MethodologyFitResult {
  /** Integer percentages that sum to 100. */
  scores: { agile: number; waterfall: number; hybrid: number };
  recommended: Methodology;
  confidence: Confidence;
  /** Overall lean in [-1, +1]: + = Agile, − = Waterfall, ~0 = balanced. */
  lean: number;
}

/** All factors at the neutral midpoint. */
export function defaultMethodologyAnswers(): MethodologyAnswers {
  const a: MethodologyAnswers = {};
  for (const f of METHODOLOGY_FACTORS) a[f.key] = 3;
  return a;
}

function clamp15(v: number): number {
  if (!Number.isFinite(v)) return 3;
  return Math.min(5, Math.max(1, v));
}

/** Round three values to integer percentages that still sum to exactly 100. */
function normalizeToPercents(a: number, w: number, h: number): { agile: number; waterfall: number; hybrid: number } {
  const total = a + w + h;
  if (total <= 1e-9) {
    // No signal at all (everything neutral) → default to Hybrid-leaning.
    return { agile: 20, waterfall: 20, hybrid: 60 };
  }
  const raw = { agile: (a / total) * 100, waterfall: (w / total) * 100, hybrid: (h / total) * 100 };
  const floors = {
    agile: Math.floor(raw.agile),
    waterfall: Math.floor(raw.waterfall),
    hybrid: Math.floor(raw.hybrid),
  };
  let remainder = 100 - (floors.agile + floors.waterfall + floors.hybrid);
  // Hand the leftover points to the largest fractional parts.
  const fracs = (['agile', 'waterfall', 'hybrid'] as const)
    .map((k) => ({ k, frac: raw[k] - floors[k] }))
    .sort((x, y) => y.frac - x.frac);
  const out = { ...floors };
  for (let i = 0; i < fracs.length && remainder > 0; i++, remainder--) out[fracs[i].k] += 1;
  return out;
}

export function computeMethodologyFit(answers: MethodologyAnswers): MethodologyFitResult {
  let agilePull = 0;
  let waterfallPull = 0;

  for (const f of METHODOLOGY_FACTORS) {
    const v = clamp15(answers[f.key] ?? 3);
    // Polarity toward the factor's Agile end, in [-1, +1].
    const towardHigh = (v - 3) / 2; // 1→-1, 3→0, 5→+1
    const agilePolarity = f.agileHigh ? towardHigh : -towardHigh;
    if (agilePolarity > 0) agilePull += f.weight * agilePolarity;
    else waterfallPull += f.weight * -agilePolarity;
  }

  const contested = Math.min(agilePull, waterfallPull);
  const agileRaw = agilePull - contested;
  const waterfallRaw = waterfallPull - contested;
  const hybridRaw = 2 * contested;

  const scores = normalizeToPercents(agileRaw, waterfallRaw, hybridRaw);

  // Recommendation = highest score (ties break Hybrid > Waterfall > Agile, the
  // safer/more-conservative choice under ambiguity).
  const ranked = [
    { m: 'Hybrid' as Methodology, s: scores.hybrid },
    { m: 'Waterfall' as Methodology, s: scores.waterfall },
    { m: 'Agile' as Methodology, s: scores.agile },
  ].sort((x, y) => y.s - x.s);
  const recommended = ranked[0].m;

  const top = ranked[0].s;
  const second = ranked[1].s;
  const gap = top > 0 ? (top - second) / top : 0;
  const totalPull = agilePull + waterfallPull;
  // No real signal (everything neutral) → honestly low confidence, regardless of
  // the default Hybrid split.
  const confidence: Confidence =
    totalPull <= 1e-9 ? 'low' : gap < 0.15 ? 'low' : gap < 0.4 ? 'moderate' : 'high';

  const lean = totalPull > 1e-9 ? (agilePull - waterfallPull) / totalPull : 0;

  return { scores, recommended, confidence, lean: Math.round(lean * 100) / 100 };
}
